import { List, Show, Create, Edit, Datagrid, TextField, DateField, ChipField, SimpleForm, TextInput, SelectInput, Button, Filter, SearchInput, useGetList, useUpdate, useNotify, useRefresh, useShowContext, ReferenceInput } from 'react-admin';
import { useState, useEffect } from 'react';
import { useEmployeeMap, getEmployeeName } from '@/hooks/useEmployeeMap';

import { Box, Typography, Paper, LinearProgress, Alert, Grid, Divider, Stepper, Step, StepLabel, Card, CardContent, List as MuiList, ListItem, ListItemIcon, ListItemText, Chip, Avatar, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery, TablePagination, CircularProgress, IconButton } from '@mui/material';
import type { ChipColor, ProgressColor } from '@/types/chip';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { TrendingUp, CheckCircle, Cancel, Schedule, ArrowForward, EmojiEvents, Warning, Person, Assignment, TrendingDown, Business, School, Category, Stars, Verified, Refresh, Close, PlaylistAddCheck } from '@mui/icons-material';
import { ListPageHeader } from '@/components/ListPageHeader';
import { WizardListToolbar } from '@/components/workflow/WizardListToolbar';

const PIP_STATUS = ['Draft', 'Active', 'Completed', 'Failed', 'Cancelled'];
const OUTCOME_TYPES = ['Performance Improved', 'Final Warning', 'Termination', 'Successful'];

interface PipRecord {
  id?: string;
  status?: string;
  pipNumber?: string;
  employee?: { id?: string };
  employeeId?: string;
  initiatedAt?: string;
  targetCompletionDate?: string;
  outcome?: string;
  performanceIssues?: string;
  improvementPlan?: string;
  reviews?: { reviewDate?: string }[];
  reviewDate?: string;
  lraChecklist?: string[];
  [key: string]: unknown;
}

interface InfoField {
  label: string;
  value: unknown;
  bold?: boolean;
  chip?: boolean;
  warning?: boolean;
}

const STATUS_COLORS: Record<string, string> = {
  Draft: 'default',
  Active: 'info',
  Completed: 'success',
  Failed: 'error',
  Cancelled: 'warning',
};

const PipProgressSparkline = ({ openedDate, targetDate, status }: { openedDate: string; targetDate: string; status: string }) => {
  const [now] = useState(() => Date.now());

  if (status === 'Completed' || status === 'Failed' || status === 'Cancelled') {
    const icon = status === 'Completed' ? <CheckCircle sx={{ fontSize: 14, color: 'success.main' }} /> :
                 <Cancel sx={{ fontSize: 14, color: 'error.main' }} />;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {icon}
        <Typography variant="caption" sx={{ color: status === 'Completed' ? 'success.main' : 'error.main', fontWeight: 600 }}>
          {status}
        </Typography>
      </Box>
    );
  }

  if (status === 'Draft') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
          Draft
        </Typography>
      </Box>
    );
  }
  const opened = new Date(openedDate).getTime();
  const target = new Date(targetDate).getTime();
  const totalDuration = target - opened;
  const elapsed = now - opened;
  const remaining = target - now;
  const daysRemaining = Math.ceil(remaining / (1000 * 60 * 60 * 24));
  const pctElapsed = Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));

  const getColor = () => {
    if (daysRemaining <= 0) return 'error';
    if (daysRemaining <= 14) return 'error';
    if (daysRemaining <= 30) return 'warning';
    return 'success';
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 120 }}>
      <Box sx={{ flex: 1, position: 'relative' }}>
        <Box sx={{ height: 8, bgcolor: 'grey.200', borderRadius: 1, overflow: 'hidden' }}>
          <Box
            sx={{
              width: `${pctElapsed}%`,
              height: '100%',
              bgcolor: `${getColor()}.main`,
              borderRadius: 1,
              transition: 'width 0.3s ease',
            }}
          />
        </Box>
        <Box sx={{ position: 'absolute', right: 0, top: -2 }}>
          <TrendingDown sx={{ fontSize: 12, color: `${getColor()}.main` }} />
        </Box>
      </Box>
      <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'right', color: `${getColor()}.main`, fontWeight: 600 }}>
        {daysRemaining <= 0 ? 'Overdue' : `${daysRemaining}d`}
      </Typography>
    </Box>
  );
};

export const PipList = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const { data: allPips, isLoading } = useGetList('pips', { pagination: { page: 1, perPage: 1000 } });
  const isMobile = useMediaQuery((theme: { breakpoints: { down: (key: string) => string } }) => theme.breakpoints.down('md'));
  const employeeMap = useEmployeeMap();

  const pips = allPips?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const stats = {
    total: allPips?.length ?? 0,
    draft: allPips?.filter((p: PipRecord) => p.status === 'Draft').length ?? 0,
    active: allPips?.filter((p: PipRecord) => p.status === 'Active').length ?? 0,
    completed: allPips?.filter((p: PipRecord) => p.status === 'Completed').length ?? 0,
    failed: allPips?.filter((p: PipRecord) => p.status === 'Failed').length ?? 0,
    cancelled: allPips?.filter((p: PipRecord) => p.status === 'Cancelled').length ?? 0,
  };

  const statusCounts = allPips?.reduce((acc: Record<string, number>, p: PipRecord) => {
    acc[p.status ?? ''] = (acc[p.status ?? ''] || 0) + 1;
    return acc;
  }, {}) ?? {};

  const pieData: { label: string; value: number }[] = Object.entries(statusCounts).map(([label, value]) => ({ label, value: value as number }));

  const durationBuckets = [30, 60, 90];
  const durationData = durationBuckets.map((days, i) => {
    const prev = i === 0 ? 0 : durationBuckets[i - 1];
    return allPips?.filter((p: PipRecord) => {
      if (!p.initiatedAt || !p.targetCompletionDate) return false;
      const duration = (new Date(p.targetCompletionDate).getTime() - new Date(p.initiatedAt).getTime()) / (1000 * 60 * 60 * 24);
      return duration > prev && duration <= days;
    }).length ?? 0;
  });
  const longDuration = allPips?.filter((p: PipRecord) => {
    if (!p.initiatedAt || !p.targetCompletionDate) return false;
    const duration = (new Date(p.targetCompletionDate).getTime() - new Date(p.initiatedAt).getTime()) / (1000 * 60 * 60 * 24);
    return duration > 90;
  }).length ?? 0;

  if (isMobile) {
    return (
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <WizardListToolbar domainId="hr.industrial-relations" wizardPath="wizard/pip" label="New PIP" />
        </Box>
        <ListPageHeader
          title="Performance Improvement Plans"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'Failed', value: String(stats.failed), color: 'error.main' },
          ]}
        />
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
          {pips?.map((pip: PipRecord) => (
            <Paper key={pip.id} sx={{ p: 2 }} onClick={() => navigate(`/pips/${pip.id}/show`)}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Avatar sx={{ width: 40, height: 40, bgcolor: STATUS_COLORS[pip.status ?? ''] as string, fontSize: 14 }}>
                    <TrendingUp sx={{ fontSize: 20 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={600}>{getEmployeeName(employeeMap, pip.employee?.id)}</Typography>
                    <Typography variant="caption" color="text.secondary">{pip.pipNumber}</Typography>
                  </Box>
                </Box>
                <Chip label={pip.status} size="small" color={STATUS_COLORS[pip.status ?? ''] as ChipColor} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                <Typography variant="caption" color="text.secondary">{pip.initiatedAt?.split('T')[0]} → {pip.targetCompletionDate?.split('T')[0]}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <PipProgressSparkline openedDate={pip.initiatedAt ?? ''} targetDate={pip.targetCompletionDate ?? ''} status={pip.status ?? ''} />
              </Box>
            </Paper>
          ))}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <WizardListToolbar domainId="hr.industrial-relations" wizardPath="wizard/pip" label="New PIP" />
      </Box>
      <ListPageHeader
        title="Performance Improvement Plans"
          metrics={[
            { label: 'Active', value: String(stats.active), color: 'info.main' },
            { label: 'Completed', value: String(stats.completed), color: 'success.main' },
            { label: 'Failed', value: String(stats.failed), color: 'error.main' },
            { label: 'Cancelled', value: String(stats.cancelled), color: 'warning.main' },
          ]}
        pieChartData={pieData}
        pieChartTitle="PIP Status Distribution"
        barChartData={[...durationData, longDuration]}
        barChartLabels={['≤30 Days', '31-60 Days', '61-90 Days', '90+ Days']}
        barChartTitle="PIP Duration Distribution"
      />
      <Paper sx={{ mt: 2, overflow: 'hidden' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell sx={{ fontWeight: 600 }}>Employee</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>PIP #</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Opened</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Target</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 140 }}>Progress</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pips?.map((pip: PipRecord) => (
              <TableRow
                key={pip.id}
                hover
                sx={{ cursor: 'pointer' }}
                onClick={() => navigate(`/pips/${pip.id}/show`)}
              >
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: STATUS_COLORS[pip.status ?? ''] as string, fontSize: 12 }}>
                      <TrendingUp sx={{ fontSize: 14 }} />
                    </Avatar>
                    <Box>
                      <Typography variant="body2" fontWeight={500}>{getEmployeeName(employeeMap, pip.employee?.id)}</Typography>
                      <Typography variant="caption" color="text.secondary">{pip.employeeId}</Typography>
                    </Box>
                  </Box>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{pip.pipNumber}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{pip.initiatedAt?.split('T')[0]}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2" color="text.secondary">{pip.targetCompletionDate?.split('T')[0]}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={pip.status} size="small" color={STATUS_COLORS[pip.status ?? ''] as ChipColor} />
                </TableCell>
                <TableCell>
                <PipProgressSparkline openedDate={pip.initiatedAt ?? ''} targetDate={pip.targetCompletionDate ?? ''} status={pip.status ?? ''} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={allPips?.length ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
        {(!pips || pips.length === 0) && (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <TrendingUp sx={{ fontSize: 48, color: 'grey.300', mb: 1 }} />
            <Typography color="text.secondary">No PIPs found</Typography>
          </Box>
        )}
      </Paper>
    </Box>
  );
};

export const PipFilters = () => (
  <Filter>
    <SearchInput source="q" placeholder="Search PIP # or employee" />
    <SelectInput source="status" choices={PIP_STATUS.map(s => ({ id: s, name: s }))} alwaysOn />
  </Filter>
);

const PIP_LRA_CHECKLIST_ITEMS = [
  { id: 'gapsDocumented', label: 'Performance gaps clearly documented' },
  { id: 'objectivesDefined', label: 'Measurable objectives defined' },
  { id: 'supportProvided', label: 'Support provided (training/coaching)' },
  { id: 'reviewTimelines', label: 'Review timelines scheduled' },
  { id: 'consequencesCommunicated', label: 'Consequences communicated' },
];

const getStepperStep = (status: string) => {
  if (status === 'Draft') return 0;
  if (status === 'Active') return 1;
  if (status === 'Completed' || status === 'Failed' || status === 'Cancelled') return 2;
  return 0;
};

const PipShowContent = () => {
  const { record } = useShowContext();
  const navigate = useNavigate();
  const [update, { isLoading }] = useUpdate();
  const notify = useNotify();
  const refresh = useRefresh();
  const employeeMap = useEmployeeMap();
  const [now] = useState(() => Date.now());

  const checklistDerived: Record<string, boolean> = {
    gapsDocumented: !!record?.performanceIssues,
    objectivesDefined: !!record?.improvementPlan,
    supportProvided: record?.status !== 'Draft' && record?.status !== 'Completed' && record?.status !== 'Failed',
    reviewTimelines: !!record?.reviewDate,
    consequencesCommunicated: record?.status !== 'Draft',
  };

  const savedList: string[] = (record as { lraChecklist?: string[] })?.lraChecklist || [];

  const getDisplayState = (): Record<string, boolean> => {
    const state: Record<string, boolean> = {};
    PIP_LRA_CHECKLIST_ITEMS.forEach(({ id }) => {
      if (savedList.includes(id)) {
        state[id] = true;
      } else if (savedList.includes(`!${id}`)) {
        state[id] = false;
      } else {
        state[id] = checklistDerived[id] ?? false;
      }
    });
    return state;
  };

  const [displayState, setDisplayState] = useState<Record<string, boolean>>(
    () => getDisplayState()
  );

  useEffect(() => {
    setDisplayState(getDisplayState());
  }, [(record as { lraChecklist?: string[]; performanceIssues?: unknown; improvementPlan?: unknown; status?: string; reviewDate?: string })?.lraChecklist, (record as { lraChecklist?: string[]; performanceIssues?: unknown; improvementPlan?: unknown; status?: string; reviewDate?: string })?.performanceIssues, (record as { lraChecklist?: string[]; performanceIssues?: unknown; improvementPlan?: unknown; status?: string; reviewDate?: string })?.improvementPlan, (record as { lraChecklist?: string[]; performanceIssues?: unknown; improvementPlan?: unknown; status?: string; reviewDate?: string })?.status, (record as { lraChecklist?: string[]; performanceIssues?: unknown; improvementPlan?: unknown; status?: string; reviewDate?: string })?.reviewDate]);

  if (!record) return null;

  const isActive = record.status === 'Active';
  const reviews = record.reviews || [];
  const latestReview = reviews.length > 0 ? reviews[reviews.length - 1] : null;
  const isOverdue = latestReview?.reviewDate && new Date(latestReview.reviewDate).getTime() < now;
  const daysSinceOpened = Math.ceil((now - new Date(record.initiatedAt).getTime()) / (1000 * 60 * 60 * 24));
  const totalDays = record.targetCompletionDate ? Math.ceil((new Date(record.targetCompletionDate).getTime() - new Date(record.initiatedAt).getTime()) / (1000 * 60 * 60 * 24)) : 90;
  const progress = Math.min(100, Math.round((daysSinceOpened / totalDays) * 100));

  const toggleChecklistItem = (id: string) => {
    const newVal = !displayState[id];
    const prev = displayState[id];
    setDisplayState(prevState => ({ ...prevState, [id]: newVal }));

    const updated: string[] = savedList.filter((x: string) => x !== id && x !== `!${id}`);
    if (newVal && !checklistDerived[id]) {
      updated.push(id);
    } else if (!newVal && checklistDerived[id]) {
      updated.push(`!${id}`);
    }

    update('pips', { id: record.id, data: { lraChecklist: updated } }, {
      onSuccess: () => { notify('Checklist updated', { type: 'success' }); },
      onError: (e: Error) => {
        notify(`Error: ${e.message}`, { type: 'error' });
        setDisplayState(prevState => ({ ...prevState, [id]: prev }));
      },
    });
  };

  const handleAction = (data: Record<string, unknown>) => {
    update('pips', { id: record.id, data }, {
      onSuccess: () => { refresh(); },
      onError: (e: Error) => { notify(`Error: ${e.message}`, { type: 'error' }); },
    });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <IconButton onClick={() => navigate('/pips')} size="small" sx={{ color: 'text.secondary' }}>
          <Close />
        </IconButton>
      </Box>
      {isActive && isOverdue && (
        <Alert severity="warning" sx={{ mb: 2 }} icon={<Warning />}>
          Review overdue — schedule follow-up meeting immediately
        </Alert>
      )}
      {record.status === 'Completed' && (
        <Alert severity="success" sx={{ mb: 2 }} icon={<EmojiEvents />}>
          PIP completed successfully
        </Alert>
      )}
      {record.status === 'Failed' && (
        <Alert severity="error" sx={{ mb: 2 }} icon={<Cancel />}>
          PIP failed — further action may be required
        </Alert>
      )}

      <Paper elevation={0} sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #1a237e 0%, #3949ab 50%, #5c6bc0 100%)', color: 'white', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56 }}>
            <TrendingUp sx={{ fontSize: 28 }} />
          </Avatar>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>{record.pipNumber || 'Performance Improvement Plan'}</Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>{getEmployeeName(employeeMap, record.employee?.id)}</Typography>
          </Box>
          <Chip label={record.status} size="medium" sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600, backdropFilter: 'blur(4px)' }} />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Stepper activeStep={getStepperStep(record.status)} sx={{ flexWrap: 'wrap' }}>
          <Step><StepLabel>Draft</StepLabel></Step>
          <Step><StepLabel>Active</StepLabel></Step>
          <Step><StepLabel>Completed / Failed</StepLabel></Step>
        </Stepper>
      </Paper>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'PIP Number', value: record.pipNumber, bold: true },
          { label: 'Employee', value: getEmployeeName(employeeMap, record.employee?.id) },
          { label: 'Status', value: record.status, chip: true },
          { label: 'Opened', value: record.initiatedAt?.split('T')[0] },
          { label: 'Target Date', value: record.targetCompletionDate?.split('T')[0] },
          { label: 'Outcome', value: record.outcome || 'In Progress' },
        ].map(f => (
          <Grid size={{ xs: 12, sm: 6, md: 3 }} key={f.label}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="caption" color="text.secondary">{f.label}</Typography>
              {f.chip ? (
                <Box sx={{ mt: 0.5 }}>
                  <Chip label={f.value} size="small" color={STATUS_COLORS[record.status] as ChipColor} />
                </Box>
              ) : (
                <Typography variant="body1" sx={{ fontWeight: f.bold ? 700 : 400, mt: 0.5, color: (f as { warning?: boolean }).warning ? 'warning.main' : 'inherit' }}>{f.value ?? '—'}</Typography>
              )}
            </Paper>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
              <Typography variant="subtitle2">PIP Progress</Typography>
              <Typography variant="caption" color="text.secondary">{daysSinceOpened} / {totalDays} days</Typography>
            </Box>
            <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 1 }} />
            <Grid container spacing={2} sx={{ mt: 1 }}>
              {[
                { label: 'Day', value: daysSinceOpened },
                { label: 'Remaining', value: Math.max(0, totalDays - daysSinceOpened) },
                { label: 'Progress', value: `${progress}%` },
              ].map(m => (
                <Grid size={{ xs: 4 }} key={m.label}>
                  <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>{m.value}</Typography>
                    <Typography variant="caption" color="text.secondary">{m.label}</Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>PIP Details</Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Person fontSize="small" color="action" />
                <Typography variant="body2">{getEmployeeName(employeeMap, record.employee?.id)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Schedule fontSize="small" color="action" />
                <Typography variant="body2">{record.initiatedAt?.split('T')[0]} → {record.targetCompletionDate?.split('T')[0]}</Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {record.performanceIssues && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'error.main' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Performance Issues (per Schedule 8 LRA)</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{record.performanceIssues}</Typography>
        </Paper>
      )}
      {record.improvementPlan && (
        <Paper sx={{ p: 2, mb: 2, borderLeft: '4px solid', borderColor: 'info.main' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>Improvement Plan</Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>{record.improvementPlan}</Typography>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Schedule 8 LRA Requirements</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>Click items to toggle their status</Typography>
        <Grid container spacing={1}>
          {PIP_LRA_CHECKLIST_ITEMS.map((check) => {
            const done = displayState[check.id] ?? false;
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={check.id}>
                <Box
                  onClick={() => toggleChecklistItem(check.id)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1,
                    cursor: 'pointer', p: 0.5, borderRadius: 1,
                    '&:hover': { bgcolor: 'action.hover' },
                    userSelect: 'none',
                  }}
                >
                  {done ? (
                    <CheckCircle color="success" fontSize="small" />
                  ) : (
                    <Schedule color="warning" fontSize="small" />
                  )}
                  <Typography variant="body2">{check.label}</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, my: 3, bgcolor: 'action.hover', borderTop: 4, borderTopColor: 'primary.main', borderLeft: 0, borderRight: 0, borderBottom: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <PlaylistAddCheck fontSize="small" color="primary" />
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Process Actions</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          {record.status === 'Draft' && (
            <Button variant="contained" color="success" size="small" startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <TrendingUp />} onClick={() => handleAction({ status: 'Active' })} disabled={isLoading}>
              Submit PIP
            </Button>
          )}
          {record.status === 'Active' && (
            <>
              <Button variant="contained" color="success" size="small" startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <CheckCircle />} onClick={() => handleAction({ status: 'Completed', outcome: 'Performance Improved' })} disabled={isLoading}>
                Record Improved
              </Button>
              <Button variant="outlined" color="error" size="small" startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <Cancel />} onClick={() => handleAction({ status: 'Failed', outcome: 'Failed' })} disabled={isLoading}>
                Record Failed
              </Button>
              <Button variant="outlined" color="warning" size="small" startIcon={isLoading ? <CircularProgress size={14} color="inherit" /> : <ArrowForward />} onClick={() => handleAction({
                status: 'Active',
                targetCompletionDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
              })} disabled={isLoading}>
                Extend PIP (30 days)
              </Button>
            </>
          )}
          {(record.status === 'Completed' || record.status === 'Failed') && (
            <Button variant="outlined" size="small" startIcon={<Assignment />} onClick={() => window.print()}>
              Print Outcome
            </Button>
          )}
          {record.outcome === 'Failed' && (
            <Button variant="contained" color="error" size="small" startIcon={<Cancel />} component={Link} to="/terminations/create" disabled={isLoading}>
              Initiate Termination
            </Button>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button component={Link} to="/disciplinary-cases" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Disciplinary Cases
        </Button>
        <Button component={Link} to="/terminations" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Terminations
        </Button>
        <Button component={Link} to="/training-records" variant="outlined" size="small" endIcon={<ArrowForward />}>
          View Training
        </Button>
        <Button component={Link} to="/employees" variant="outlined" size="small">
          View Employee
        </Button>
      </Box>
    </Box>
  );
};

export const PipShow = () => (
  <Show>
    <PipShowContent />
  </Show>
);

export const PipCreate = () => {
  const [searchParams] = useSearchParams();
  const employeeId = searchParams.get('employeeId') || '';
  const employeeName = searchParams.get('employeeName') || '';

  return (
    <Create>
      <SimpleForm>
        <Alert severity="info" sx={{ mb: 3 }}>
          PIP must meet Schedule 8 LRA requirements — measurable objectives and support plan required
        </Alert>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <TrendingUp fontSize="small" /> PIP Details
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextInput source="caseNumber" label="PIP Number" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <ReferenceInput source="employee.id" reference="employees" fullWidth defaultValue={employeeId}>
                <SelectInput optionText="name" label="Employee" />
              </ReferenceInput>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <TextInput source="targetCompletionDate" label="Target Completion Date" type="date" fullWidth />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <SelectInput source="status" choices={PIP_STATUS.map(s => ({ id: s, name: s }))} fullWidth defaultValue="Draft" label="Status" />
            </Grid>
          </Grid>
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Warning fontSize="small" /> Performance Issues (Schedule 8)
          </Typography>
          <TextInput source="performanceIssues" multiline fullWidth label="Performance gaps — be specific and measurable" placeholder="Describe where the employee is falling short of required standards..." sx={{ '& .MuiInputBase-root': { minHeight: 100 } }} />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Stars fontSize="small" /> Improvement Plan with Measurable Objectives
          </Typography>
          <TextInput source="improvementPlan" multiline fullWidth label="Support and objectives" placeholder="1. Training to be provided:&#10;2. Measurable objectives for each gap:&#10;3. Support (coaching, resources):&#10;4. Review schedule:" sx={{ '& .MuiInputBase-root': { minHeight: 120 } }} />
        </Paper>

        <Paper variant="outlined" sx={{ p: 3 }}>
          <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
            <Verified fontSize="small" /> Support Actions
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 4 }}>
              <SelectInput source="trainingProvided" choices={['Yes', 'No', 'Planned'].map(t => ({ id: t, name: t }))} fullWidth label="Training to be Provided" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <SelectInput source="coachingAssigned" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="Coaching Assigned" />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <SelectInput source="managerInvolved" choices={[{ id: 'Yes', name: 'Yes' }, { id: 'No', name: 'No' }]} fullWidth label="Manager Involved" />
            </Grid>
          </Grid>
        </Paper>
      </SimpleForm>
    </Create>
  );
};

export const PipEdit = () => (
  <Edit>
    <SimpleForm>
      <Alert severity="warning" sx={{ mb: 3 }}>
        All updates to PIP are logged — ensure compliance with Schedule 8 LRA
      </Alert>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Schedule fontSize="small" /> PIP Status & Dates
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectInput source="status" choices={PIP_STATUS.map(s => ({ id: s, name: s }))} fullWidth label="Status" />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextInput source="targetCompletionDate" label="Target Completion" type="date" fullWidth />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <CheckCircle fontSize="small" /> Review Outcome
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="gapsAddressed" choices={['All Met', 'Partially Met', 'Not Met'].map(g => ({ id: g, name: g }))} fullWidth label="Gaps Addressed" />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <SelectInput source="outcome" choices={OUTCOME_TYPES.map(o => ({ id: o, name: o }))} fullWidth label="PIP Outcome" />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrendingUp fontSize="small" /> Progress Notes
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="performanceIssues" multiline fullWidth label="Performance Issues" sx={{ '& .MuiInputBase-root': { minHeight: 100 } }} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="improvementPlan" multiline fullWidth label="Improvement Plan" sx={{ '& .MuiInputBase-root': { minHeight: 100 } }} />
          </Grid>
        </Grid>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 2.5, color: 'primary.main', fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Assignment fontSize="small" /> Final Review
        </Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="finalReviewDate" label="Final Review Date" type="date" fullWidth />
          </Grid>
          <Grid size={{ xs: 12, sm: 6 }}>
            <TextInput source="finalOutcome" label="Final Outcome Notes" fullWidth />
          </Grid>
        </Grid>
      </Paper>
    </SimpleForm>
  </Edit>
);